import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface PaymentStatusProps {
  success?: boolean;
}

const PaymentStatus: React.FC<PaymentStatusProps> = ({ success }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  
  // Get payment status from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const paymentStatus = queryParams.get('payment');
  
  // Use either the prop or the query parameter
  const isSuccess = success !== undefined ? success : paymentStatus === 'success';
  const isCancelled = success === false || paymentStatus === 'cancelled';
  
  // Don't show if there's no payment status, but still set up the effect
  const shouldDisplay = isSuccess || isCancelled;
  
  // Set up countdown for redirect - this is now unconditional to follow Hook rules
  useEffect(() => {
    // Only run the timer logic if we should display
    if (shouldDisplay) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            
            // Remove payment parameter from URL or redirect to dashboard
            if (success !== undefined) {
              navigate('/dashboard', { replace: true });
            } else {
              const newUrl = window.location.pathname;
              navigate(newUrl, { replace: true });
            }
            
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [isSuccess, isCancelled, navigate, success, shouldDisplay]);
  
  // Don't render anything if we shouldn't display
  if (!shouldDisplay) {
    return null;
  }
  
  // Render success message
  if (isSuccess) {
    return (
      <div className="fixed inset-x-0 top-0 z-50 flex justify-center">
        <div className="bg-green-500 text-white px-6 py-3 rounded-b-lg shadow-lg flex items-center max-w-xl">
          <svg className="h-6 w-6 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <div className="flex-grow">
            <p className="font-medium">Payment successful!</p>
            <p className="text-sm opacity-90">Your subscription has been activated. Enjoy your premium features.</p>
          </div>
          <div className="ml-4 bg-white bg-opacity-20 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
            {countdown}
          </div>
        </div>
      </div>
    );
  }
  
  // Render cancellation message
  return (
    <div className="fixed inset-x-0 top-0 z-50 flex justify-center">
      <div className="bg-yellow-500 text-white px-6 py-3 rounded-b-lg shadow-lg flex items-center max-w-xl">
        <svg className="h-6 w-6 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="flex-grow">
          <p className="font-medium">Payment cancelled</p>
          <p className="text-sm opacity-90">Your subscription was not activated. You can try again anytime.</p>
        </div>
        <div className="ml-4 bg-white bg-opacity-20 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
          {countdown}
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus; 